import { isArrayEqual } from 'app/items/shared/util';
import { Asset } from 'app/shared/collections/asset';
import { ImsObject } from 'app/shared/collections/ims_object';
import { IpDevice } from 'app/shared/collections/ip_device';
import { AnyFieldSchemaType, FieldSchemaUnits } from 'app/shared/collections/StatDefinition';
import { ImsTimeCollection } from 'app/shared/collections/time/ims_time_collection.factory';
import { Zone } from 'app/shared/collections/zones/zone';
import { MapOptionsService } from 'app/shared/map_options/MapOptionsService';
import { numericalOperators, objectTypeOperators, Operator } from 'app/shared/operators';
import { UnitService } from 'app/shared/unit.service';
import { IMSAggregator } from 'app/views/aggregators/aggregator';
import { ILabelGetter } from 'app/views/value_getters/ILabelGetter';
import { ColourObject } from 'app/views/view_types/network/value_getters/associated_ap_value_getter';
import * as _ from 'lodash';

/**
 * A function to filter stats!
 */
export type StatFilterFn = (stat: any) => boolean;

/**
 * A function that generates a function that is used to filter stats.
 * This is done to avoid looking up the same filter data over and over e.g. the current asset options.
 */
export type StatFilterFnGenerator = () => StatFilterFn;

export type ValueGetterValueType = ColourObject | string | number | null | undefined;

/**
 * Might refactor in the future so this is used for the outer most key as well. For now it's not worth it.
 * Only used for the inner keys / filters
 */
export interface StatGetter {
    statKey: string;
    statFilterFnGenerator?: StatFilterFnGenerator;
}

// All this stuff is synchronous so it should be ok to use module based variables for scratch values
let scratchI;
let getValueForStat_tempStats;
let getValueForIPDeviceStats;
let getValueForZoneStats;

/**
 * @ngdoc service
 * @name ims.map.service:IMSValueGetter
 * @description
 * The IMSValueGetter class is used to return a value when given a stat or asset/time.
 *
 */
export class IMSValueGetter<ValueType = ValueGetterValueType> implements ILabelGetter {
    statCollection: ImsTimeCollection;
    statKey: string;
    units: FieldSchemaUnits;
    operators: Operator[];
    placeholder: string;
    imsType: string;

    protected _assetOptionsService: MapOptionsService;

    // hot code, minimise allocations
    protected _scratch_ImsValueGetter_getValueForAsset_ipKey: any;
    protected _scratch_ImsValueGetter_getValueForAsset_value: any;
    protected _scratch_ImsValueGetter_getValueForAsset_values: any;
    protected _scratch_ImsValueGetter_getValueForAsset_ipDevice: any;
    protected _scratch_ImsValueGetter_getValueForIPDevice_stats: any;
    protected _scratch_ImsValueGetter_getValueForZone_stats: any;

    // Used for filtering out stats at the outer most level e.g. is the ip device stat ignored or not
    statFilterFnGenerator: StatFilterFnGenerator;

    // Use this for getting out nested stats. e.g. ipDeviceStat.radioStats()
    // The array is traversed, each one for a deeper level of nesting
    protected innerStatGetters: StatGetter[];

    /**
     * Create a new IMSValueGetter.
     *
     * Note the slightly janky ctor args at the moment. It would be nicer to pass in a single
     * StatGetter array instead of a statKey + statFilterFnGenerator for the root level and then
     * an array for every nested level after that...
     * That would need to touch a lot of code at the moment for no real benefit. If we expand on this
     * idea in the future beyond the NetworkViewController, consider the refactor.
     *
     * @param statCollection
     * @param {string} statKey
     * @param {string} units
     * @param {StatFilterFnGenerator} statFilterFnGenerator
     * @param {StatGetter[]} innerStatGetters
     * @param operators
     * @param {string} placeholder
     * @param imsType
     */
    constructor(
        statCollection: ImsTimeCollection = null,
        statKey: string = null,
        units: FieldSchemaUnits = null,
        statFilterFnGenerator: StatFilterFnGenerator = null,
        innerStatGetters: StatGetter[] = [],
        operators: Operator[] = [],
        placeholder: string = '',
        imsType: string = null,
    ) {
        //// Public Variables ////

        /**
         * @ngdoc property
         * @name statCollection
         * @propertyOf ims.map.service:IMSValueGetter
         * @description
         * The optional statCollection of this IMSValueGetter (eg ipDeviceStatCollection)
         * This is used when grabbing time/asset
         */
        this.statCollection = statCollection;

        /**
         * @ngdoc property
         * @name statKey
         * @propertyOf ims.map.service:IMSValueGetter
         * @description
         * The optional statKey of this IMSValueGetter (eg 'connectivity')
         * This can be passed through to enable instantiating value getters with a different key
         * instead of subclassing to create new ones
         */
        this.statKey = statKey;

        /**
         * @ngdoc property
         * @name units
         * @propertyOf ims.map.service:IMSValueGetter
         * @description
         * The units used for this particular IMSVisualiser (eg, 'dBm')
         */
        this.units = units || null; // if units is falsey, use null so the function know not to use it

        /**
         * @ngdoc property
         * @name operators
         * @propertyOf ims.map.service:IMSValueGetter
         * @description
         * List of operators used when filtering on this value
         */
        this.operators = operators;

        /**
         * @ngdoc property
         * @name placeholder
         * @propertyOf ims.map.service:IMSValueGetter
         * @description
         * The placeholder used for text field input
         */
        this.placeholder = placeholder;

        /**
         * @ngdoc property
         * @name imsType
         * @propertyOf ims.map.service:IMSValueGetter
         * @description
         * This should only be set for connected asset and ip device for heatmap filtering
         */
        this.imsType = imsType;

        // When these fnGenerators return null, the filtering part is skipped during get stats
        this.statFilterFnGenerator = statFilterFnGenerator ? statFilterFnGenerator : () => null;
        this.innerStatGetters = innerStatGetters.map((sg) => ({
            statKey: sg.statKey,
            statFilterFnGenerator: sg.statFilterFnGenerator ? sg.statFilterFnGenerator : () => null,
        }));
    }

    getAllowedOperators() {
        return this.operators;
    }

    /**
     * Takes an IPDevice and a cesium time and returns a formatted value
     *
     * @param ipDevice An IPDevice object
     * @param time Cesium JulianDate object
     * @param aggregator
     * @returns a number or string value
     */
    getFormattedValueForIPDevice(
        ipDevice: IpDevice,
        time: Cesium.JulianDate,
        aggregator: IMSAggregator,
    ) {
        if (!aggregator) {
            throw new Error('An aggregator is required when calling getFormattedValueForIPDevice');
        }
        let value: any = this.getValueForIPDevice(ipDevice, time, aggregator);
        return this.getFormattedValue(value);
    }

    getFormattedValue(value: any) {
        if (value == null || typeof value === 'undefined') {
            return null;
        }
        const { value: v, unit } = UnitService.convertValue(this.units, value);
        return `${v}${unit}`;
    }

    /**
     * Takes an Asset, a cesium time, and an aggregator and returns a formatted value
     *
     * @param asset An Asset object
     * @param time Cesium JulianDate object
     * @param aggregator IMSAggregator instance
     * @returns a number or string value
     */
    getFormattedValueForAsset(
        asset: Asset,
        time: Cesium.JulianDate,
        aggregator: IMSAggregator,
    ): string | null {
        const value = this.getValueForAsset(asset, time, aggregator);
        if (!!value && aggregator.logicalName === 'count') {
            return value.toString();
        }
        return this.getFormattedValue(value);
    }

    /**
     * Takes a Zone, a cesium time, and an aggregator and returns a formatted value
     *
     * @param zone
     * @param time Cesium JulianDate object
     * @param aggregator IMSAggregator instance
     * @returns a number or string value
     */
    getFormattedValueForZone(zone: Zone, time: Cesium.JulianDate, aggregator: IMSAggregator) {
        const value = this.getValueForZone(zone, time, aggregator);
        return this.getFormattedValue(value);
    }

    /**
     * Returns the type of input field to show, text or number.
     *
     * @returns the field type
     */
    getFieldType(): AnyFieldSchemaType {
        if (
            isArrayEqual(this.operators, numericalOperators) ||
            isArrayEqual(this.operators, objectTypeOperators) ||
            _.isEmpty(this.operators)
        ) {
            return 'number';
        }

        return 'string';
    }

    _createInnerFilterFns(): StatFilterFn[] {
        return this.innerStatGetters.map((sg) => sg.statFilterFnGenerator());
    }
    /**
     * Gets an array of stats for an ims zone
     *
     * @param zone IMSZone
     * @param time Cesium JulianDate object
     * @returns {any[]} An array of stat objects
     * @private
     */
    _getStatForZone(zone: Zone, time: Cesium.JulianDate): object[] {
        const statFilterFn = this.statFilterFnGenerator();

        if (!zone && !this.statCollection) {
            return undefined;
        }

        // take our first list of stats
        getValueForZoneStats = this.statCollection.getStats(zone.id, time);
        if (statFilterFn) {
            // apply a filter if need be
            getValueForZoneStats = getValueForZoneStats.filter(statFilterFn);
        }

        return getValueForZoneStats;
    }

    /**
     * Takes a Zone and a cesium time and returns a value
     *
     * @param zone A Zone object
     * @param time Cesium JulianDate object
     * @param aggregator
     * @returns a number or string value
     */
    getValueForZone(
        zone: Zone,
        time: Cesium.JulianDate,
        aggregator: IMSAggregator,
    ): ValueGetterValueType {
        if (!aggregator) {
            console.error(`IMSValueGetter.getValueForZone cannot be called without aggregator`);
            return undefined;
        }

        const stats = this._getStatForZone(zone, time);
        if (!stats) {
            return undefined;
        }

        this._scratch_ImsValueGetter_getValueForZone_stats = stats.map(
            (stat) => stat[this.statKey],
        );
        return aggregator.getAggValueForValueArray(
            this._scratch_ImsValueGetter_getValueForZone_stats,
        );
    }

    /**
     * Takes an IPDevice and a cesium time and returns a stat (IMS Object).
     *
     * @param ipDevice An IPDevice IMS object
     * @param time Cesium JulianDate object
     * @param statFilterFn
     * @param innerStatFilterFns A list of inner stat filter functions. A null value means no filter
     * @returns {null} An IMS stat Object
     */
    _getStatForIPDevice(
        ipDevice: IpDevice,
        time: Cesium.JulianDate,
        statFilterFn: StatFilterFn,
        innerStatFilterFns: StatFilterFn[],
    ): ImsObject[] {
        if (!ipDevice) {
            return [];
        }

        // take our first list of stats
        getValueForIPDeviceStats = this.statCollection.getStats(ipDevice.id, time);

        if (statFilterFn) {
            // apply a filter if need be
            getValueForIPDeviceStats = getValueForIPDeviceStats.filter(statFilterFn);
        }

        // the idea here is that each index of the innerStatGetters array operates on
        // one level deeper of the nested stats.
        // so we start with the initialStats as the first list
        this.innerStatGetters.forEach((statGetter, i) => {
            // we set the the next list of stats to the result of applying the first inner stat getter
            getValueForIPDeviceStats = _.flatten(
                getValueForIPDeviceStats
                    .map((s) => s[statGetter.statKey])
                    .filter((value) => value !== undefined),
            );
            if (innerStatFilterFns[i]) {
                // if this innerStatGetter has an associated filter fn (by array index), apply it
                getValueForIPDeviceStats = getValueForIPDeviceStats.filter(innerStatFilterFns[i]);
            }
        });

        // after each inner stat getter has been applied, innerStats will be the final depth of stats
        return getValueForIPDeviceStats;
    }

    /**
     * Takes an IPDevice and a cesium time and returns a value
     *
     * @param ipDevice An IPDevice object
     * @param time Cesium JulianDate object
     * @param aggregator
     * @returns a number or string value
     */
    getValueForIPDevice(
        ipDevice: IpDevice,
        time: Cesium.JulianDate,
        aggregator: IMSAggregator,
    ): ValueGetterValueType {
        if (!aggregator) {
            console.error(`IMSValueGetter.getValueForIPDevice cannot be called without aggregator`);
            return undefined;
        }

        const statFilterFn = this.statFilterFnGenerator();
        const innerStatFilterFns = this._createInnerFilterFns();

        const stats = this._getStatForIPDevice(ipDevice, time, statFilterFn, innerStatFilterFns);
        if (!stats?.length) {
            return undefined;
        }

        this._scratch_ImsValueGetter_getValueForIPDevice_stats = stats.map(
            (stat) => stat[this.statKey],
        );

        return aggregator.getAggValueForValueArray(
            this._scratch_ImsValueGetter_getValueForIPDevice_stats,
        );
    }

    /**
     * Gets an array of stats for an ims asset
     *
     * @param asset IMSAsset
     * @param time Cesium JulianDate object
     * @returns {any[]} An array of stat objects
     * @private
     */
    _getStatForAsset(asset: Asset, time: Cesium.JulianDate): object[] {
        const statFilterFn = this.statFilterFnGenerator();
        const innerStatFilterFns = this._createInnerFilterFns();

        if (!asset && !this.statCollection) {
            return undefined;
        }

        let stats = [];
        const ipDeviceKeys = Object.keys(asset.ipDevices);
        for (let i = 0; i < ipDeviceKeys.length; ++i) {
            let ipDevice = asset.ipDevices[ipDeviceKeys[i]];

            let stat = this._getStatForIPDevice(ipDevice, time, statFilterFn, innerStatFilterFns);
            if (stat) {
                stats.push(stat);
            }
        }

        if (stats.length === 0) {
            return undefined;
        }

        return stats;
    }

    /**
     * Takes an asset, cesium time and an IMSAggregator instance, and returns a value
     *
     * @param asset An Asset object (IMS Asset, not Cesium Entity)
     * @param time Cesium JulianDate object
     * @param aggregator IMSAggregator instance
     * @returns a number or string value
     */
    getValueForAsset(
        asset: Asset,
        time: Cesium.JulianDate,
        aggregator: IMSAggregator,
    ): ValueGetterValueType {
        if (!aggregator) {
            console.error(`IMSValueGetter.getValueForAsset cannot be called without aggregator`);
            return undefined;
        }

        if (!asset || !asset.ipDevices) {
            return undefined;
        }

        this._scratch_ImsValueGetter_getValueForAsset_values = [];
        for (this._scratch_ImsValueGetter_getValueForAsset_ipKey in asset.ipDevices) {
            this._scratch_ImsValueGetter_getValueForAsset_ipDevice =
                asset.ipDevices[this._scratch_ImsValueGetter_getValueForAsset_ipKey];

            this._scratch_ImsValueGetter_getValueForAsset_value = this.getValueForIPDevice(
                this._scratch_ImsValueGetter_getValueForAsset_ipDevice,
                time,
                aggregator,
            );

            if (this._scratch_ImsValueGetter_getValueForAsset_value === undefined) {
                continue;
            }

            this._scratch_ImsValueGetter_getValueForAsset_values.push(
                this._scratch_ImsValueGetter_getValueForAsset_value,
            );
        }

        if (this._scratch_ImsValueGetter_getValueForAsset_values.length === 0) {
            return undefined;
        }

        return aggregator.getAggValueForValueArray(
            this._scratch_ImsValueGetter_getValueForAsset_values,
        );
    }

    /**
     * Takes in any IMSObject and returns a value.
     * The default behaviour is to use the statKey
     *
     * IMPORTANT: The stat should be of the same type as is returned by the statCollection that is
     * given to this ValueGetter object upon construction.
     *
     * Note no filters are applied for this method unlike the _getStatsForIPDevice method.
     *
     * There isn't really a good reason for this except "thats how things work right now". You'll note that
     * the places where this function is used one of 2 things happen:
     * 1. ValueGetter has been sub classed and this method has been overwritten
     * 2. It's a situation where currently the filterFnGenerator and innerFilterFnGenerator are undefined.
     *
     * @param stat An IMSValueGetter
     * @param aggregator
     * @returns a number or string value
     */
    getValueForStat(stat, aggregator: IMSAggregator = null): ValueGetterValueType {
        // no stat, do nothing
        if (!stat) {
            return null; // TODO: null here
        }

        // there's data but no aggregator- so this is the "none" aggregator / no aggregation
        if (!(this.innerStatGetters.length > 0 && aggregator)) {
            return stat[this.statKey];
        }

        // Look for inner stats
        getValueForStat_tempStats = [stat];
        for (scratchI = 0; scratchI < this.innerStatGetters.length; ++scratchI) {
            getValueForStat_tempStats = _.flatten(
                getValueForStat_tempStats
                    .map((s) => s[this.innerStatGetters[scratchI].statKey])
                    .filter((value) => value !== undefined),
            );
        }

        if (!(getValueForStat_tempStats.length > 0)) {
            return undefined; // TODO: but undefined here?
        }

        // Finally got to the end, return the array of inner stats.
        return aggregator.getAggValueForValueArray(
            getValueForStat_tempStats.map((s) => s[this.statKey]),
        );
    }
}
